import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';

import {
  useDroneService,
  useOrganisationService,
  useUserService
} from 'services';
import { useService } from 'hooks';
import {
  IGetDroneReferenceDataRequest,
  IGetDroneReferenceDataResponse,
  IGetDroneRateCardsRequest,
  IGetDroneRateCardsResponse,
  IGetIndividualDronesRequest,
  IGetIndividualDronesResponse,
  IGetOrganisationDronesRequest,
  IGetOrganisationDronesResponse
} from 'services/droneService/droneServiceTypes';

import {
  IBaseOrganisationRequest,
  IGetOrganisationActiveSubmissionResponse,
  ISubmissionDetails
} from 'services/organisationService/organisationServiceTypes';
import {
  IGetIndividualActiveSubmissionResponse
} from 'services/userService/userServiceTypes';

import { DRONE_CONSTANTS } from 'internalConstants';
import { featureSelector, urlSelector } from 'PortalStore/Selectors/EnvDataSelectors';
import { getPortalUrl } from 'components/Portal/Utilities';
import { IFormAlertType } from 'types/forms/formTypes';

const getDroneReferenceDataRequest: IGetDroneReferenceDataRequest = {
  type: ['Drone Weight Class 2021'],
  code: ['DRONE_MANUFACTURER', 'DRONE_MODEL', 'DRONE_CATEGORY', 'DRONE_WEIGHT_CLASS']
};

const getDroneRateCardsRequest: IGetDroneRateCardsRequest = {
  FeeCode: ['DRONE_R_COM', 'DRONE_R_REC']
};

interface IDroneContextType {
  isOrganisationRegistration: boolean;
  referenceData?: IGetDroneReferenceDataResponse | null | undefined;
  rateCards?: IGetDroneRateCardsResponse | null | undefined;
  inProgressRegistrationSubmissions: Array<ISubmissionDetails>;
  inProgressRenewalSubmissions: Array<ISubmissionDetails>;
  isFeatureRecDrone: boolean;
  featureDroneSerialNumberValidationIsActive: boolean;
  dashboardUrl: string;
  manageDronesUrl: string;
  individualDrones?: IGetIndividualDronesResponse | null | undefined;
  organisationDrones?: IGetOrganisationDronesResponse | null | undefined;
  contextAlert?: IFormAlertType | null;
  submissionInProgress: boolean;
  droneTableSorted: boolean;
  checkSerialNumberPrefix: boolean;

  checkIfOtherManufacturer: (manufacturerCode: string | undefined | null) => boolean;
  checkIfOtherModel: (modelCode: string | undefined | null) => boolean;
  checkIfHomeBuilt: (manufacturerCode: string | undefined | null) => boolean;
  checkIfAirship: (categoryCode: string | undefined | null) => boolean;
  setContextAlert: (alert: IFormAlertType | null) => void;
  setSubmissionInProgress: (submissionInProgress: boolean) => void;
  setDroneTableSorted: (droneTableSorted: boolean) => void;
  setCheckSerialNumberPrefix: (checkSerialNumberPrefix: boolean) => void;
}

export const DroneContext = React.createContext<IDroneContextType>({} as IDroneContextType);

interface IDroneContextProviderProps {
  organisationId?: string;
}

export const DroneContextProvider: React.FC<IDroneContextProviderProps> = ({ organisationId, children }) => {
  const droneService = useDroneService();
  const organisationService = useOrganisationService();
  const userService = useUserService();

  const [contextAlert, setContextAlert] = useState<IFormAlertType | null>(null);
  const [submissionInProgress, setSubmissionInProgress] = useState<boolean>(false);
  const [droneTableSorted, setDroneTableSorted] = useState<boolean>(false);

  const B2CSettings = useSelector((state: any) => state.B2CSettings);
  const urlHome = useSelector((state: any) => urlSelector(state, 'Portal - Home URL'));

  const [checkSerialNumberPrefix, setCheckSerialNumberPrefix] = useState<boolean>(false);

  const referenceDataResponse = useService<IGetDroneReferenceDataResponse>(() => droneService.getDroneReferenceData(getDroneReferenceDataRequest));
  const rateCardsResponse = useService<IGetDroneRateCardsResponse>(() => droneService.getDroneRateCards(getDroneRateCardsRequest));
  const individualDronesResponse = useService<IGetIndividualDronesResponse>(() => droneService.getIndividualDrones({ types: ['recreational', 'commercial'] } as IGetIndividualDronesRequest));
  const organisationDronesResponse = useService<IGetOrganisationDronesResponse>(() => droneService.getOrganisationDrones({ organisationId: organisationId ?? '', types: ['recreational', 'commercial'] } as IGetOrganisationDronesRequest));
  let activeDroneSubmissions: IGetOrganisationActiveSubmissionResponse | null = null;

  if (organisationId) {
    activeDroneSubmissions = useService<IGetOrganisationActiveSubmissionResponse>(() => organisationService.getActiveSubmissions({ organisationId: organisationId } as IBaseOrganisationRequest));
  }
  else {
    activeDroneSubmissions = useService<IGetIndividualActiveSubmissionResponse>(() => userService.getActiveSubmissions());
  }

  // set flag for recreational drones feature
  const featureRecDrone = useSelector((state: any) => featureSelector(state, 'Portal - Feature Availability - Recreational Drone'));
  const isFeatureRecDrone = featureRecDrone ? moment().isSameOrAfter(moment(featureRecDrone, 'DD/MM/YYYY')) : false;

  // set flag for drone serial number validation feature availability
  const featureDroneSerialNumberValidation = useSelector((state: any) => featureSelector(state, 'Portal - Feature Availability - Drone Serial Validation'));
  const featureDroneSerialNumberValidationIsActive = featureDroneSerialNumberValidation ? moment().isSameOrAfter(moment(featureDroneSerialNumberValidation, 'DD/MM/YYYY')) : false;

  const checkIfOtherManufacturer = (manufacturerCode: string | undefined | null) => manufacturerCode === DRONE_CONSTANTS.OTHER_MANUFACTURER_CODE;
  const checkIfOtherModel = (modelCode: string | undefined | null) => !!modelCode && modelCode.endsWith(DRONE_CONSTANTS.OTHER_MODEL_CODE_EXT);
  const checkIfHomeBuilt = (manufacturerCode: string | undefined | null) => manufacturerCode === DRONE_CONSTANTS.HOME_BUILT_MANUFACTURER_CODE;
  const checkIfAirship = (categoryCode: string | undefined | null) => categoryCode === DRONE_CONSTANTS.AIRSHIP_CATEGORY_CODE;

  const getDashboardUrl = () => {
    if (!organisationId || organisationId === '') {
      return getPortalUrl('/dashind', B2CSettings.items.standalone, urlHome);
    }
    const jparams = { 'organisationId': organisationId };
    return getPortalUrl('/dashorg', B2CSettings.items.standalone, urlHome) + '&params=' + encodeURIComponent(btoa(JSON.stringify(jparams))) + '&casaorgid=' + organisationId;
  };
  const dashboardUrl = getDashboardUrl();

  const getManageDronesUrl = () => {
    if (!organisationId || organisationId === '') {
      return `${window.location.origin}/forms/indmanagerpa?standalone=${B2CSettings.items.standalone}`;
    }
    return `${window.location.origin}/forms/orgmanagerpa?standalone=${B2CSettings.items.standalone}&casaorgid=${organisationId}`;
  };
  const manageDronesUrl = getManageDronesUrl();

  return (
    <DroneContext.Provider value={{
      isOrganisationRegistration: !!organisationId,
      referenceData: referenceDataResponse,
      rateCards: rateCardsResponse,
      inProgressRegistrationSubmissions: activeDroneSubmissions?.value?.inprogdrones ?? [],
      inProgressRenewalSubmissions: activeDroneSubmissions?.value?.inprogrenewaldrones ?? [],
      isFeatureRecDrone: isFeatureRecDrone,
      featureDroneSerialNumberValidationIsActive: featureDroneSerialNumberValidationIsActive,
      dashboardUrl: dashboardUrl,
      manageDronesUrl: manageDronesUrl,
      individualDrones: individualDronesResponse,
      organisationDrones: organisationDronesResponse,
      contextAlert: contextAlert,
      submissionInProgress: submissionInProgress,
      droneTableSorted: droneTableSorted,
      checkSerialNumberPrefix: checkSerialNumberPrefix,

      checkIfOtherManufacturer,
      checkIfOtherModel,
      checkIfHomeBuilt,
      checkIfAirship,
      setContextAlert,
      setSubmissionInProgress,
      setDroneTableSorted,
      setCheckSerialNumberPrefix
    }}>
      {children}
    </DroneContext.Provider>
  );
};